/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useRef } from 'react';
import {
    object, func, bool, arrayOf,
    shape, string, number,
} from 'prop-types';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import ReactHtmlParser from 'react-html-parser';
import ReactMarkdown from 'react-markdown';
import { withRouter } from 'react-router';
import Button from '@material-ui/core/Button';
import {
    Dialog, DialogContent, DialogTitle, IconButton, useMediaQuery,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIosTwoTone';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIosTwoTone';
import EventDispatcher from '../../../state/reducers/EventDispatcher';
import { isEmpty } from '../../../utils/object';
import { decode as htmlDecode } from '../../../utils/htmlEntity';
import configEnv from '../../../config';
import { emitCustomTrackingEvent } from '../../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';

const { PERSIST_KEY } = configEnv;

const styles = (theme) => ({
    modalBox: {
        maxWidth: '950px',
        width: '100%',
        borderRadius: 'inherit',
        '& .MuiDialogTitle-root': {
            padding: '0px 24px',
            boxShadow: '0px 3px 3px rgb(47 47 47 / 15%)',
            '@media only screen and (max-width: 600px) ': {
                padding: '10px',
            },
        },
        '@media only screen and (max-width: 600px) ': {
            margin: '2rem 0.6rem',
        },
    },
    dialogTitle: {
        fontSize: '1.25rem',
        lineHeight: '1.75rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontWeight: 700,
        '@media only screen and (max-width: 1023px) ': {
            fontSize: '1rem',
            lineHeight: '1.25rem',
        },
        '@media only screen and (max-width: 400.5px) ': {
            fontSize: '0.875rem',
            lineHeight: '1rem',
        },

    },
    singleContent: {
        display: 'flex',
        gap: '17px',
        '&::-webkit-scrollbar-thumb': {
            borderRadius: '10px',
            backgroundColor: 'rgba(156, 156, 156, 1)',  // scroll bar color
        },
        '&::-webkit-scrollbar': {  /* Works on scrollbar for Chrome, Safari and Opera */
            height: '2px',
            width: '10px',
            backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-track': {
            borderRadius: '10px',
            backgroundColor: 'transparent',
        },
        '@media only screen and (max-width: 600px) ': {
            display: 'block',
        },
    },
    productName: {
        fontSize: '1.75rem',
        lineHeight: '2.5rem',
        fontWeight: 700,
        '@media only screen and (max-width: 1023px) ': {
            fontSize: '1.25rem',
            lineHeight: '1.875rem',
        },
    },
    skuCode: {
        fontSize: '0.875rem',
        lineHeight: '1.125rem',
        padding: '5px 0px',
    },
    price: {
        fontSize: '1.25rem',
        lineHeight: '1.75rem',
        fontWeight: 700,
        '@media only screen and (max-width: 1023px) ': {
            fontSize: '1.125rem',
        },
        '@media only screen and (max-width: 600px) ': {
            fontSize: '1rem',
            lineHeight: '1.25rem',
        },
    },
    crossedPrice: {
        fontWeight: 700,
        textDecoration: 'line-through',
        fontSize: '1.25rem',
        lineHeight: '1.75rem',
        '@media only screen and (max-width: 1023px) ': {
            fontSize: '1.125rem',
        },
        '@media only screen and (max-width: 600px) ': {
            fontSize: '1rem',
            lineHeight: '1.25rem',
        },
    },
    discountPrice: {
        color: theme.palette.cms?.errorZipVerifyPDP || '#A1001A',
        paddingLeft: '7px',
        fontWeight: 700,
        fontSize: '1.25rem',
        lineHeight: '1.75rem',
        '@media only screen and (max-width: 1023px) ': {
            fontSize: '1.125rem',
        },
        '@media only screen and (max-width: 600.5px) ': {
            fontSize: '1rem',
            lineHeight: '1.25rem',
        },
    },
    content: {
        borderTop: `1px solid ${theme.palette.cms?.black || '#2f2f2f'}`,
        padding: '1rem',
        marginTop: '1rem',
        borderBottom: `1px solid ${theme.palette.cms?.black || '#2f2f2f'}`,
        height: '220px',
        overflowY: 'scroll',
        '@media only screen and (max-width: 768.5px) ': {
            height: '135px',
        },
        '@media only screen and (max-width: 600.5px) ': {
            padding: '16px 0px',
            overflowY: 'unset',
            height: 'auto',
        },
        '&::-webkit-scrollbar-thumb': {
            borderRadius: '10px',
            backgroundColor: 'rgba(156, 156, 156, 1)',  // scroll bar color
        },
        '&::-webkit-scrollbar': {  /* Works on scrollbar for Chrome, Safari and Opera */
            height: '2px',
            width: '10px',
            backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-track': {
            borderRadius: '10px',
            backgroundColor: 'transparent',
        },
        '& ul': {
            fontSize: '1rem',
            paddingLeft: '1rem',
            lineHeight: '1.5rem',
            margin: '11px 0px',
            '@media only screen and (max-width: 600.5px) ': {
                paddingLeft: '30px',
            },
        },
    },
    contentTitle: {
        fontWeight: 700,
        lineHeight: '1.25rem',
    },
    buttonContainer: {
        backgroundColor: 'rgba(161, 0, 26, 1)',
    },
    button: {
        color: theme.palette.cms?.white || 'white',
        padding: '8px 70px 9px',
        fontSize: '1rem',
        lineHeight: '1.25rem',
        fontWeight: 700,
        '@media only screen and (max-width: 900.5px) ': {
            padding: '8px ',
        },
    },
    arrowButton: {
        backgroundColor: theme.palette?.cms?.activeButtonColor || '#E8F1ED',
        borderRadius: '50%',
        width: '28px',
        height: '28px',
        '@media only screen and (max-width: 380.5px) ': {
            width: '20px',
            height: '20px',
        },
        '& svg': {
            width: '0.7em',
            fill: theme.palette.cms?.black || '#2f2f2f',
            '@media only screen and (max-width: 380.5px) ': {
                width: '0.5em',
            },
        },
        '&:hover': {
            backgroundColor: theme.palette?.cms?.activeButtonColor || '#E8F1ED',
        },
    },
    iconContainer: {
        display: 'flex',
        gap: '1.25rem',
        justifyContent: 'center',
        alignItems: 'center',
        '@media only screen and (max-width: 600.5px) ': {
            gap: '0.6rem',
        },
    },
    ButtonMainContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '38px',
        marginTop: '21px',
        '@media only screen and (max-width: 1023.5px) ': {
            gap: '16px',
        },
        '@media only screen and (max-width: 600.5px) ': {
            justifyContent: 'center',
        },
    },
    viewDetails: {
        display: 'flex',
        alignItems: 'center',
        fontSize: '1rem',
        lineHeight: '1.25rem',
        fontWeight: 700,
        textDecoration: 'underline',
        cursor: 'pointer',
        color: theme.palette.cms?.black || '#2f2f2f',
        '& svg': {
            width: '0.6em',
        },
    },
    dialogContent: {
        padding: '30px 20px 29px 32px',
        '@media only screen and (max-width: 600.5px) ': {
            padding: '1.25rem',
        },
    },
    descriptionWrapper: {
        width: '100%',
    },
    imageContainer: {
        width: '70%',
        aspectRatio: ' 230/259',
        '@media only screen and (max-width: 767.5px) ': {
            width: '100%',
        },
        '& img': {
            width: '100%',
        },
    },
    buttonMobileContainer: {
        backgroundColor: 'rgba(161, 0, 26, 1)',
        position: 'sticky',
        bottom: '0px',
        width: 'auto',
        textAlign: 'center',
        margin: '1.25rem',
    },
    closeBtn: {
        '@media only screen and (max-width: 600.5px) ': {
            padding: '3px',
        },
        '& svg': {
            fill: theme.palette.cms?.black || '#2f2f2f',
            '@media only screen and (max-width: 380.5px) ': {
                width: '0.8em',
            },
        },
    },
    hideButton: {
        visibility: 'hidden',
    },
});

const GiftListProductDetails = ({
    product, recipient, history,
    history: { location: { pathname: giftListURL } },
    remainingRecipient,
    handleConfirmGiftListShopTheSiteClicked,
    handleGiftListAddToCart,
    emitCustomClickTrackingEvent,
    classes, isReadyState, open, setOpen, setData, isFetchingModal,
}) => {
    if (isEmpty(product)) return null;
    const {
        customAttributes: { personalization, parentProduct },
        customAttributes,
    } = product;
    const isMobile = useMediaQuery('(max-width: 600.5px)');

    useEffect(() => {
        if (open) {
            if (typeof document !== 'undefined'
                && document.getElementById('root')?.style?.position !== 'static') {
                document.getElementById('root').style.position = 'static';
            }
        } else if (typeof document !== 'undefined'
            && document.getElementById('root')?.style?.position === 'static') {
            document.getElementById('root').style.position = 'unset';
        }
    }, [open]);

    const ageVerifyFlag = (!isEmpty(customAttributes) && customAttributes?.ageVerifyFlag) ? customAttributes.ageVerifyFlag : 'false';
    const personalizationType = (!isEmpty(personalization) && personalization?.personalizationType) ? personalization.personalizationType : null;
    const personalizationTemplate = (!isEmpty(personalization) && personalization?.personalizationTemplate) ? personalization.personalizationTemplate : 'N';
    const specialProductNames = ['pick', 'choose', 'wine'];
    const specialProduct = ageVerifyFlag !== 'false'
            || parentProduct?.productType === 'CLUB'
            || parentProduct?.productType === 'COLLECTION'
            || parentProduct?.productType === 'CUSTOM_ASSORTMENT'
            || (
                !['REGULAR_PRODUCT', 'REGULARPRODUCT'].includes(parentProduct?.productType)
                && (
                    personalizationTemplate === 'Y'
                    || personalizationType === 'G'
                    || personalizationType === '0'
                )
            )
            || specialProductNames.some((pName) => product.name.toLowerCase().includes(pName));
    const recipientWithCounter = { ...recipient, remainingRecipient, giftListURL };
    const handleAddToCartClicked = () => {
        let sku = product.id;
        if (product?.id?.includes('-')) {
            sku = product.id.split('-')[2];
        }
        if (specialProduct) {
            const { seoproductDisplayURL } = product;
            handleConfirmGiftListShopTheSiteClicked(
                recipientWithCounter,
                seoproductDisplayURL || customAttributes?.parentProduct?.seo?.url || product?.seo?.url,
                false,
                customAttributes?.deliveryIndicator,
                customAttributes?.productType,
            );
            if (seoproductDisplayURL || product?.seo?.url) history.push(seoproductDisplayURL || product?.seo?.url);
            else if (customAttributes?.parentProduct?.seo?.url) history.push(customAttributes?.parentProduct?.seo?.url);
        } else {
            handleGiftListAddToCart(sku, recipient, true);
        }
        emitCustomClickTrackingEvent({
            action: 'Click',
            partNumber: product?.oGPartNumber,
            experimentTracking: product?.experimentTracking,
            page: { type: 'gift-list' },
            eventSubType: 'campaign-tracking',
        });
    };
    const innerScroll = useRef();
    // this will render quick view contents for club items from aggregator response
    const clubQuickViewContents = () => {
        const clubItemContents = product?.customAttributes?.clubItem?.components || [];
        return (
            <ul className="itemContentsMarkDown">
                {
                    clubItemContents?.map((club) => (
                        <li className="marginBottom">{club.month} : {club.name} </li>
                    ))
                }
            </ul>
        );
    };

    const singleProductDetailTemplate = () => {
        const productName = product.name || '';
        const newName = htmlDecode(productName) || '';
        const imageURL = (product.image) ? product.image.replace(/^http[s]?:/, 'https:') : 'https://images.contentstack.io/v3/assets/blt95e0138311135214/bltffae3c14fd3096e8/62e01cf13bba407090b248ee/Missing_Image.png?auto=webp';
        const productItemContents = product.itemContents || '';
        const hasItemContentStyle = productItemContents?.includes('style=') || productItemContents?.includes('<p><strong>');

        return (
            <>
                <div className={classes.contentWrapper}>
                    <div className={classes.singleContent}>
                        <div className={classes.imageContainer}>
                            <img
                                className={classes.image}
                                src={`${imageURL}?auto=webp`}
                                alt={newName}
                            />
                        </div>
                        <div className={classes.descriptionWrapper}>
                            <div>
                                <div className={classes.productName}>{ReactHtmlParser(newName)}</div>
                                <div className={classes.skuCode}>{product.sku || product.id}</div>
                                <div>
                                    {product.discountPrice ? (
                                        <>
                                            <span className={classes.crossedPrice}>
                                                {product.crossedPrice}
                                            </span>
                                            <span className={classes.discountPrice}>
                                                ${product.discountPrice}
                                            </span>
                                        </>
                                    ) : (
                                        <span className={classes.price}>${product.price}</span>
                                    )}
                                </div>
                                <div className="bold small">{product.status}</div>
                            </div>
                            { productItemContents
                                ?                            (
                                    <div className={classes.content} ref={innerScroll}>
                                        <div className={classes.contentTitle}><span>Contents</span></div>
                                        <ReactMarkdown source={productItemContents} escapeHtml={false} className={hasItemContentStyle ? classes.itemContentStyleMark : ''} />
                                    </div>
                                )
                                : clubQuickViewContents() }
                            <div className={classes.ButtonMainContainer}>
                                <div
                                    className={classes.viewDetails}
                                    role="button"
                                    tabIndex="-1"
                                    onClick={() => {
                                        handleConfirmGiftListShopTheSiteClicked(
                                            recipientWithCounter,
                                            product?.seoproductDisplayURL,
                                            false,
                                        );
                                        history?.push(product?.seoproductDisplayURL);
                                    }}
                                    onKeyDown={() => { }}
                                >VIEW DETAILS <ArrowForwardIosIcon />
                                </div>
                                {!isMobile && (
                                    <div className={classes.buttonContainer}>
                                        <Button
                                            className={classes.button}
                                            onClick={() => {
                                                handleAddToCartClicked();
                                                if (isFetchingModal) { setOpen(false); }
                                            }}
                                        >
                                            {isReadyState
                                                ? 'Add To Cart'
                                                : 'Adding...'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </>
        );
    };
    const indexMatch = recipient.products.findIndex((item) => item.id === product.id);
    const reciepientProduct = recipient?.products || [];
    const scrollRef = useRef();
    return (
        <Dialog
            classes={{
                paper: classes.modalBox,
            }}
            open={open}
            onClose={() => setOpen(false)}
        >
            <DialogTitle>
                <div className={classes.dialogTitle}>
                    OTHER RECOMMENDATIONS
                    <div className={classes.iconContainer}>
                        <IconButton
                            className={indexMatch <= 0 ? `${classes.hideButton} ${classes.arrowButton}` : classes.arrowButton}
                            aria-label="previous"
                            onClick={() => {
                                setData(reciepientProduct[indexMatch - 1]);
                                if (scrollRef.current) {
                                    scrollRef.current.scrollTop = 0;
                                }
                                if (innerScroll.current) {   // for desktop view inner content scroll
                                    innerScroll.current.scrollTop = 0;
                                }
                            }}
                        >
                            <ArrowBackIosIcon />
                        </IconButton>
                        <IconButton
                            className={indexMatch <= reciepientProduct?.length - 2 ? classes.arrowButton : `${classes.hideButton} ${classes.arrowButton}`}
                            aria-label="next"
                            onClick={() => {
                                setData(reciepientProduct[indexMatch + 1]);
                                if (scrollRef.current) {
                                    scrollRef.current.scrollTop = 0;
                                }
                                if (innerScroll.current) { // for desktop view inner content scroll
                                    innerScroll.current.scrollTop = 0;
                                }
                            }}
                        >
                            <ArrowForwardIosIcon />
                        </IconButton>
                        <IconButton
                            className={classes.closeBtn}
                            aria-label="Close"
                            onClick={() => {
                                setOpen(false);
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                </div>
            </DialogTitle>
            <DialogContent className={classes.dialogContent} ref={scrollRef}>
                {singleProductDetailTemplate(specialProduct)}

            </DialogContent>
            {isMobile &&  (
                <div className={classes.buttonMobileContainer}>
                    <Button
                        className={classes.button}
                        onClick={() => {
                            handleAddToCartClicked();
                            if (isFetchingModal) { setOpen(false); }
                        }}
                    >
                        {isReadyState
                            ? 'Add To Cart'
                            : 'Adding...'}
                    </Button>
                </div>
            )}
        </Dialog>

    );
};

GiftListProductDetails.defaultProps = {
    product: {},
    handleConfirmGiftListShopTheSiteClicked: () => { },
    history: {},
    emitCustomClickTrackingEvent: () => { },
};

GiftListProductDetails.propTypes = {
    classes: object.isRequired,
    history: object,
    product: shape({
        id: string,
        image: string,
        name: string,
        partNumber: string,
        price: number,
        sku: string,
        status: string,
    }),
    recipient: shape({
        AddressBookEntryId: string,
        FirstName: string,
        LastName: string,
        EmailAddress: string,
        LastUpdateDate: string,
        PhoneNumber: string,
        Addresses: arrayOf(
            shape({
                AddressId: number,
                AddressBookEntryId: string,
                AddressType: string,
                AddressLineOne: string,
                AddressLineTwo: string,
                City: string,
                PostalCode: string,
                Country: string,
            }),
        ),
    }).isRequired,
    isReadyState: bool.isRequired,
    handleGiftListAddToCart: func.isRequired,
    remainingRecipient: number.isRequired,
    handleConfirmGiftListShopTheSiteClicked: func,
    emitCustomClickTrackingEvent: func,
    open: bool.isRequired,
    setOpen: func.isRequired,
    setData: func.isRequired,
    isFetchingModal: bool.isRequired,
};

const mapStateToProps = (state) => {
    const newState = state[PERSIST_KEY] ? state[PERSIST_KEY] : state;
    return {
        isReadyState: newState.isReadyState,
        remainingRecipient: newState.giftList.remainingRecipient,
        isFetchingModal: newState?.isReadyState,
    };
};

const mapDispatchToProps = (dispatch) => ({
    handleGiftListAddToCart: (productId, recipient, closeModal) => dispatch(EventDispatcher.handleGiftListAddToCart(productId, recipient, closeModal)),
    handleConfirmGiftListShopTheSiteClicked: (recipient, url, dontShowModal, deliveryIndicator, productType) => dispatch(EventDispatcher.handleConfirmGiftListShopTheSiteClicked(
        recipient,
        url,
        dontShowModal,
        deliveryIndicator,
        productType,
    )),
    emitCustomClickTrackingEvent: (payload) => dispatch(emitCustomTrackingEvent(payload)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withRouter(withStyles(styles)(GiftListProductDetails)));
